import { z } from 'zod';

export const WEEKDAYS = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const WeekdaySchema = z.enum(WEEKDAYS);
export type Weekday = (typeof WEEKDAYS)[number];

export const SCHEDULER_MANAGED_BY = ['internal', 'external'] as const;
export const SchedulerManagedBySchema = z.enum(SCHEDULER_MANAGED_BY);
export type SchedulerManagedBy = (typeof SCHEDULER_MANAGED_BY)[number];

const TIME_REGEX = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

export const ScheduleTimeSlotSchema = z
  .object({
    id: z.string(),
    startTime: z.string().regex(TIME_REGEX, 'Invalid time format. Use HH:MM (24-hour)'),
    endTime: z.string().regex(TIME_REGEX, 'Invalid time format. Use HH:MM (24-hour)'),
    powerSetpoint: z.number(),
    isActive: z.boolean().optional().default(true),
    validUntil: z.string().nullable().optional(),
  })
  .refine(
    (data) => {
      const [startHour, startMin] = data.startTime.split(':').map(Number);
      const [endHour, endMin] = data.endTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;
      return endMinutes > startMinutes;
    },
    {
      message: 'End time must be after start time',
      path: ['endTime'],
    }
  );

export type ScheduleTimeSlot = z.infer<typeof ScheduleTimeSlotSchema>;

export const WeeklyScheduleSchema = z.object({
  Monday: z.array(ScheduleTimeSlotSchema).default([]),
  Tuesday: z.array(ScheduleTimeSlotSchema).default([]),
  Wednesday: z.array(ScheduleTimeSlotSchema).default([]),
  Thursday: z.array(ScheduleTimeSlotSchema).default([]),
  Friday: z.array(ScheduleTimeSlotSchema).default([]),
  Saturday: z.array(ScheduleTimeSlotSchema).default([]),
  Sunday: z.array(ScheduleTimeSlotSchema).default([]),
});

export type WeeklySchedule = z.infer<typeof WeeklyScheduleSchema>;

export const PowerScheduleSchema = z.object({
  newlyAdded: z.boolean().optional(),
  wasEdited: z.boolean().optional(),

  companyId: z
    .string({ error: 'Company ID is required' })
    .min(1, 'Company ID cannot be empty'),
  plantId: z.string({ error: 'Plant ID is required' }).min(1, 'Plant ID cannot be empty'),
  scheduleId: z
    .string({ error: 'Schedule ID is required' })
    .min(1, 'Schedule ID cannot be empty'),

  scheduleName: z.string().min(1, 'Schedule name is required').max(100),
  description: z.string().max(500).optional(),

  currentStatus: z.enum(['started', 'stopped', 'paused']).default('stopped'),
  isActive: z.boolean().default(true),

  origin: SchedulerManagedBySchema.default('internal'),
  lastManagedBy: SchedulerManagedBySchema.default('internal'),

  weeklySchedule: WeeklyScheduleSchema,

  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  createdBy: z.string().optional(),
  updatedBy: z.string().optional(),
  version: z.number().int().min(1).default(1),
});

export type PowerSchedule = z.infer<typeof PowerScheduleSchema>;
